/**
 * Consenso ai cookie: cosa ha scelto l'utente e come dirlo a Google.
 *
 * Due categorie oltre ai cookie tecnici, che non chiedono consenso:
 * statistiche (Google Analytics) e marketing (Google Ads, Meta). La scelta
 * vale sei mesi, come chiedono le linee guida del Garante, e si puo'
 * cambiare quando si vuole da "Preferenze cookie" nel pie' di pagina.
 *
 * I tag partono da Google Tag Manager con Consent Mode v2: in index.html
 * tutto e' "negato" finche' non arriva un aggiornamento, e l'aggiornamento
 * lo manda `applicaConsenso`. Lo stesso index.html rilegge la scelta salvata
 * prima ancora che parta React, cosi' chi ha gia' scelto non aspetta.
 *
 * Funzioni pure con storage iniettabile, per le prove.
 */

export const CHIAVE_CONSENSO = "obscura_consenso";
export const DURATA_CONSENSO_MS = 180 * 24 * 60 * 60 * 1000;
export const VERSIONE_CONSENSO = 1;

export const CATEGORIE = [
  {
    chiave: "statistiche",
    nome: "Statistiche",
    descrizione: "Google Analytics: quante persone visitano il sito e come lo usano, in forma aggregata.",
  },
  {
    chiave: "marketing",
    nome: "Marketing",
    descrizione: "Google Ads e Meta: per capire se le campagne portano visite e per mostrare annunci pertinenti.",
  },
];

function storage(s) {
  if (s) return s;
  try { return window.localStorage; } catch (_) { return null; }
}

/**
 * Il codice con cui la scelta e' registrata sui nostri sistemi (vedi
 * registraConsenso). Nasce con la prima scelta e sopravvive ai rinnovi e
 * alle scelte successive, cosi' il registro racconta la storia di un solo
 * browser. Casuale: non identifica nessuno.
 */
function leggiCodice(s) {
  try {
    const d = JSON.parse(storage(s)?.getItem(CHIAVE_CONSENSO) || "null");
    return typeof d?.id === "string" && /^[0-9a-fA-F-]{16,64}$/.test(d.id) ? d.id : null;
  } catch (_) {
    return null;
  }
}

function nuovoCodice() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (_) { /* si ricade sotto */ }
  let esito = "";
  while (esito.length < 32) esito += Math.floor(Math.random() * 16).toString(16);
  return esito;
}

/** @returns {{statistiche: boolean, marketing: boolean, ts: number, id: string|null}|null} */
export function leggiConsenso(s, adesso = Date.now()) {
  try {
    const raw = storage(s)?.getItem(CHIAVE_CONSENSO);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || d.v !== VERSIONE_CONSENSO || !Number.isFinite(d.ts)) return null;
    if (adesso - d.ts > DURATA_CONSENSO_MS) return null;
    return { statistiche: d.statistiche === true, marketing: d.marketing === true, ts: d.ts, id: leggiCodice(s) };
  } catch (_) {
    return null;
  }
}

export function salvaConsenso({ statistiche = false, marketing = false }, s, adesso = Date.now()) {
  const scelta = {
    v: VERSIONE_CONSENSO, statistiche: Boolean(statistiche), marketing: Boolean(marketing), ts: adesso,
    id: leggiCodice(s) || nuovoCodice(),
  };
  try { storage(s)?.setItem(CHIAVE_CONSENSO, JSON.stringify(scelta)); } catch (_) { /* niente storage: si richiedera' */ }
  return { statistiche: scelta.statistiche, marketing: scelta.marketing, ts: scelta.ts, id: scelta.id };
}

/**
 * Il registro dei consensi: una riga sui nostri sistemi per ogni scelta,
 * con codice, categorie, versione del banner e da dove viene la scelta.
 * E' la prova del consenso che le linee guida del Garante chiedono al
 * titolare. Non blocca niente: se la rete manca, la scelta vale lo stesso
 * nel browser.
 * @returns {Promise<boolean>} vero se il backend ha registrato
 */
export function registraConsenso(scelta, origine = "banner", f = typeof fetch === "function" ? fetch : null) {
  if (!f || !scelta?.id) return Promise.resolve(false);
  try {
    return f("/api/consenso", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        codice: scelta.id, statistiche: Boolean(scelta.statistiche), marketing: Boolean(scelta.marketing),
        versione: VERSIONE_CONSENSO, origine: origine === "preferenze" ? "preferenze" : "banner",
      }),
    }).then((r) => Boolean(r && r.ok)).catch(() => false);
  } catch (_) {
    return Promise.resolve(false);
  }
}

export function dimenticaConsenso(s) {
  try { storage(s)?.removeItem(CHIAVE_CONSENSO); } catch (_) { /* noop */ }
}

/** I segnali di Google Consent Mode v2 per una scelta. */
export function segnaliGoogle({ statistiche = false, marketing = false }) {
  const si = (v) => (v ? "granted" : "denied");
  return {
    analytics_storage: si(statistiche),
    ad_storage: si(marketing),
    ad_user_data: si(marketing),
    ad_personalization: si(marketing),
    functionality_storage: "granted",
    security_storage: "granted",
  };
}

/**
 * Comunica la scelta ai tag: aggiornamento del Consent Mode e un evento nel
 * dataLayer, cosi' Tag Manager puo' far partire subito i tag appena consentiti.
 */
export function applicaConsenso(scelta, w = typeof window !== "undefined" ? window : null) {
  if (!w) return;
  w.dataLayer = w.dataLayer || [];
  // Stessa forma del gtag di index.html: i comandi di consenso vanno nel
  // dataLayer come `arguments`, non come array, o Tag Manager non li legge.
  const gtag = typeof w.gtag === "function" ? w.gtag : function () { w.dataLayer.push(arguments); };
  gtag("consent", "update", segnaliGoogle(scelta));
  w.dataLayer.push({
    event: "consenso_aggiornato",
    consenso_statistiche: Boolean(scelta.statistiche),
    consenso_marketing: Boolean(scelta.marketing),
  });
}

/** Nome dell'evento con cui il pie' di pagina chiede di riaprire il banner. */
export const EVENTO_PREFERENZE = "obscura:preferenze-cookie";

export function apriPreferenzeCookie(w = typeof window !== "undefined" ? window : null) {
  if (!w || typeof w.dispatchEvent !== "function") return;
  w.dispatchEvent(new CustomEvent(EVENTO_PREFERENZE));
}
